import PropTypes from 'prop-types';

import useRequest from 'core/hooks/useRequest';
import { bebop as bebopApi } from 'core/api/definitions/bebop';

import { resolve as resolveEnv } from 'core/utils/env';
import { denormalizeData } from 'core/utils/api';
import pluralize from 'core/utils/pluralize';
import { processQueryResults } from 'core/utils/query';

import ReleaseImage from './ReleaseImage';
import ReleaseTitle from './ReleaseTitle';
import ReleaseInfo from './ReleaseInfo';

import styles from './index.styl';

const qutoServiceEndpoint = resolveEnv({
  '*': 'https://stage.quto.ru/unity/v1/catalog/releases/',
  'production': 'https://quto.ru/unity/v1/catalog/releases/',
});

const qutoBebopEndpoint = resolveEnv({
  '*': 'https://s1.quto.ru/api/v2/topics',
  'production': 'https://quto.ru/api/v2/topics',
});


/**
 * @author Aminev Ramil <r.aminev@rambler-co.ru>
 */

function QutoRelease(props) {
  const {
    block,
    release: outerRelease,
    topicsCount: outerTopicsCount,
    parentProps,
  } = props;
  const {
    teaser_params: {
      brand,
      model,
      generation,
      submodel,
    },
  } = block.attributes;

  const {
    theme,
  } = parentProps;

  const results = useRequest({ queries: [{
    queryKey: [qutoServiceEndpoint, {
      'filter[brand]': brand,
      'filter[model]': model,
      'filter[generation]': generation,
      'filter[body]': submodel,
      include: 'car_brand,car_model',
      'relations[car_release]': 'brand,model',
      'attributes[car_release]': 'base,image,stats',
      'attributes[car_brand]': 'base,stats',
      'attributes[car_model]': 'base,stats',
    }],
    queryFn: ({ queryKey: [path, params] }) => bebopApi.getExternal(path, params).catch(() => null),
    select: releases => denormalizeData(releases)?.[0],
    enabled: process.env.BROWSER_RUNTIME && !outerRelease,
  }, {
    queryKey: [qutoBebopEndpoint, {
      tag: brand,
      fields: '',
      with_filtered_count: 1,
    }],
    queryFn: ({ queryKey: [path, params] }) => bebopApi.getExternal(path, params).catch(() => 0),
    select: data => data?.meta?.filtered_count,
    enabled: process.env.BROWSER_RUNTIME && !!brand && !outerTopicsCount,
  }] });

  const { data: [
    release,
    count = 0,
  ], isLoading } = processQueryResults(results);

  if (isLoading) return <div className={styles.loader} />;

  if (!release) return null;

  const topicsCount = outerTopicsCount || count;
  const {
    attributes: {
      url,
      teaser_params: {
        brand: brandSlug,
      },
    },
  } = block;

  const {
    attributes: {
      image,
      name,
      generation_offers_url: offersUrl,
      modifications_count: modificationsCount,
      release_offers_new_prices: {
        min: minPrice,
      },
    },
  } = release;

  const {
    attributes: {
      versions: {
        original: imageSrc,
      },
    },
  } = image;

  const modificationsText = `${modificationsCount} ${pluralize(modificationsCount, ['модификация', 'модификации', 'модификаций'])}`;
  const topicsText = `${topicsCount} ${pluralize(topicsCount, ['материал', 'материалa', 'материалов'])}`;

  const imageProps = {
    url,
    imageSrc,
    parentProps,
  };

  const titleProps = {
    url,
    name,
    parentProps,
  };

  const infoProps = {
    offersUrl,
    brandSlug,
    modificationsText,
    minPrice,
    url,
    topicsText,
    parentProps,
  };

  return (
    <div className={styles.teaser}>
      <style jsx>{`
        .${styles.teaser}
          border-top 1px solid ${theme.colors.divider}
          border-bottom 1px solid ${theme.colors.divider}
      `}</style>
      <div className={styles.image}>
        <ReleaseImage
          {...imageProps}
        />
      </div>
      <div className={styles.headline}>
        <ReleaseTitle
          {...titleProps}
        />
      </div>
      <div className={styles.info}>
        <ReleaseInfo
          {...infoProps}
        />
      </div>
    </div>
  );
}

QutoRelease.propTypes = {
  block: PropTypes.object,
  release: PropTypes.object,
  topicsCount: PropTypes.number,
  parentProps: PropTypes.object,
};


export default QutoRelease;
